// Multi-tab coordination using BroadcastChannel
// Ensures cache consistency and queue safety across tabs
// Enhanced with sync-specific coordination

import { OfflineConfig } from '../config/offline-config.js';

class MultiTabCoordinator {
  constructor() {
    this.channel = null;
    this.listeners = new Map();
    this.tabId = this.generateTabId();
    this.isPrimary = false;
    this.knownTabs = new Map(); // Track known tabs with timestamps
    this.leadershipTimer = null;
  }

  /**
   * Generate unique tab ID
   */
  generateTabId() {
    return `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize coordination
   */
  initialize() {
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('[MultiTab] BroadcastChannel not supported');
      return;
    }

    this.channel = new BroadcastChannel(OfflineConfig.multiTab.channelName);
    this.channel.addEventListener('message', this.handleMessage.bind(this));

    // Announce presence
    this.broadcast({ type: 'TAB_JOINED', tabId: this.tabId });

    // Check if we should be primary
    this.checkPrimaryStatus();

    // Start leadership heartbeat
    this.startLeadershipHeartbeat();

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => this.destroy());
  }

  /**
   * Handle incoming messages
   */
  handleMessage(event) {
    const { type, tabId, data } = event.data;

    // Ignore own messages
    if (tabId === this.tabId) return;

    switch (type) {
      case 'TAB_JOINED':
        this.handleTabJoined(tabId);
        break;
      case 'TAB_PRESENT':
        this.handleTabPresent(tabId);
        break;
      case 'TAB_LEFT':
        this.handleTabLeft(tabId);
        break;
      case 'CACHE_INVALIDATED':
        this.handleCacheInvalidated(data);
        break;
      case 'ENTITY_UPDATED':
        this.handleEntityUpdated(data);
        break;
      case 'MUTATION_QUEUED':
        this.handleMutationQueued(data);
        break;
      case 'QUEUE_PROCESSED':
        this.handleQueueProcessed(data);
        break;
      case 'PRIMARY_CLAIM':
        this.handlePrimaryClaim(tabId);
        break;
      case 'PRIMARY_HEARTBEAT':
        this.handlePrimaryHeartbeat(tabId);
        break;
      case 'SYNC_STARTED':
        this.handleSyncStarted(data);
        break;
      case 'SYNC_STOPPED':
        this.handleSyncStopped(data);
        break;
    }

    // Notify listeners
    this.notifyListeners(type, data);
  }

  /**
   * Handle tab joined
   */
  handleTabJoined(tabId) {
    console.log('[MultiTab] Tab joined:', tabId);
    this.knownTabs.set(tabId, Date.now());
    
    // Respond with our presence
    this.broadcast({ type: 'TAB_PRESENT', tabId: this.tabId });
    
    // If we're primary, announce leadership
    if (this.isPrimary) {
      this.broadcast({ type: 'PRIMARY_CLAIM', tabId: this.tabId });
    }
  }

  /**
   * Handle tab present
   */
  handleTabPresent(tabId) {
    this.knownTabs.set(tabId, Date.now());
    console.log('[MultiTab] Tab present:', tabId);
  }

  /**
   * Handle tab left
   */
  handleTabLeft(tabId) {
    console.log('[MultiTab] Tab left:', tabId);
    this.knownTabs.delete(tabId);
    
    // If primary left, we might need to become primary
    if (!this.isPrimary) {
      this.checkPrimaryStatus();
    }
  }

  /**
   * Handle cache invalidated
   */
  handleCacheInvalidated(data) {
    const { cacheKey, entityType } = data;
    console.log('[MultiTab] Cache invalidated:', cacheKey);
    // Trigger local cache invalidation
    this.notifyListeners('CACHE_INVALIDATED', data);
  }

  /**
   * Handle entity updated
   */
  handleEntityUpdated(data) {
    const { entityType, entityId } = data;
    console.log('[MultiTab] Entity updated:', entityType, entityId);
    // Trigger local entity refresh
    this.notifyListeners('ENTITY_UPDATED', data);
  }

  /**
   * Handle mutation queued
   */
  handleMutationQueued(data) {
    const { mutationId } = data;
    console.log('[MultiTab] Mutation queued:', mutationId);
    // Notify local queue
    this.notifyListeners('MUTATION_QUEUED', data);
  }

  /**
   * Handle queue processed
   */
  handleQueueProcessed(data) {
    const { count } = data;
    console.log('[MultiTab] Queue processed:', count);
    // Refresh local data
    this.notifyListeners('QUEUE_PROCESSED', data);
  }

  /**
   * Handle primary claim
   */
  handlePrimaryClaim(tabId) {
    if (this.isPrimary && tabId !== this.tabId) {
      // Another tab claims to be primary - check if we should yield
      // For now, simple heuristic: we yield if they have a lower tab ID
      if (tabId < this.tabId) {
        this.isPrimary = false;
        console.log('[MultiTab] Yielded leadership to:', tabId);
        this.stopLeadershipHeartbeat();
      }
    } else if (!this.isPrimary) {
      this.isPrimary = false;
      console.log('[MultiTab] Primary claimed by:', tabId);
    }
  }

  /**
   * Handle primary heartbeat
   */
  handlePrimaryHeartbeat(tabId) {
    if (this.isPrimary && tabId !== this.tabId) {
      // Another tab is sending heartbeats as primary - yield
      this.isPrimary = false;
      console.log('[MultiTab] Yielding leadership due to heartbeat from:', tabId);
      this.stopLeadershipHeartbeat();
    }
    
    // Update known tab timestamp
    this.knownTabs.set(tabId, Date.now());
  }

  /**
   * Handle sync started
   */
  handleSyncStarted(data) {
    console.log('[MultiTab] Sync started in tab:', data.tabId);
    this.notifyListeners('SYNC_STARTED', data);
  }

  /**
   * Handle sync stopped
   */
  handleSyncStopped(data) {
    console.log('[MultiTab] Sync stopped in tab:', data.tabId);
    this.notifyListeners('SYNC_STOPPED', data);
  }

  /**
   * Check if we should be primary
   */
  async checkPrimaryStatus() {
    // If no other tabs known, become primary
    if (this.knownTabs.size === 0) {
      this.isPrimary = true;
      this.broadcast({ type: 'PRIMARY_CLAIM', tabId: this.tabId });
      this.startLeadershipHeartbeat();
      console.log('[MultiTab] Became primary (no other tabs)');
      return;
    }

    // Simple heuristic: lowest tab ID is primary
    const allTabIds = [this.tabId, ...Array.from(this.knownTabs.keys())];
    const lowestId = allTabIds.sort()[0];
    
    if (lowestId === this.tabId) {
      this.isPrimary = true;
      this.broadcast({ type: 'PRIMARY_CLAIM', tabId: this.tabId });
      this.startLeadershipHeartbeat();
      console.log('[MultiTab] Became primary (lowest ID)');
    } else {
      this.isPrimary = false;
      this.stopLeadershipHeartbeat();
    }
  }

  /**
   * Start leadership heartbeat
   */
  startLeadershipHeartbeat() {
    if (this.leadershipTimer) {
      clearInterval(this.leadershipTimer);
    }

    this.leadershipTimer = setInterval(() => {
      if (this.isPrimary) {
        this.broadcast({ type: 'PRIMARY_HEARTBEAT', tabId: this.tabId });
      }
    }, OfflineConfig.multiTab.syncInterval);
  }

  /**
   * Stop leadership heartbeat
   */
  stopLeadershipHeartbeat() {
    if (this.leadershipTimer) {
      clearInterval(this.leadershipTimer);
      this.leadershipTimer = null;
    }
  }

  /**
   * Broadcast message to all tabs
   */
  broadcast(message) {
    if (!this.channel) return;
    
    const fullMessage = {
      ...message,
      tabId: this.tabId,
      timestamp: Date.now(),
    };

    try {
      this.channel.postMessage(fullMessage);
    } catch (error) {
      console.error('[MultiTab] Broadcast failed:', error);
    }
  }

  /**
   * Subscribe to events
   */
  subscribe(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(eventType)?.delete(callback);
    };
  }

  /**
   * Notify listeners
   */
  notifyListeners(eventType, data) {
    const listeners = this.listeners.get(eventType);
    if (!listeners) return;

    listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('[MultiTab] Listener error:', error);
      }
    });
  }

  /**
   * Notify cache invalidation
   */
  notifyCacheInvalidated(cacheKey, entityType) {
    this.broadcast({
      type: 'CACHE_INVALIDATED',
      data: { cacheKey, entityType },
    });
  }

  /**
   * Notify entity update
   */
  notifyEntityUpdated(entityType, entityId) {
    this.broadcast({
      type: 'ENTITY_UPDATED',
      data: { entityType, entityId },
    });
  }

  /**
   * Notify mutation queued
   */
  notifyMutationQueued(mutationId) {
    this.broadcast({
      type: 'MUTATION_QUEUED',
      data: { mutationId },
    });
  }

  /**
   * Notify queue processed
   */
  notifyQueueProcessed(count) {
    this.broadcast({
      type: 'QUEUE_PROCESSED',
      data: { count },
    });
  }

  /**
   * Notify sync started
   */
  notifySyncStarted() {
    this.broadcast({
      type: 'SYNC_STARTED',
      data: { tabId: this.tabId },
    });
  }

  /**
   * Notify sync stopped
   */
  notifySyncStopped() {
    this.broadcast({
      type: 'SYNC_STOPPED',
      data: { tabId: this.tabId },
    });
  }

  /**
   * Get known tabs
   */
  getKnownTabs() {
    return Array.from(this.knownTabs.keys());
  }

  /**
   * Get tab count
   */
  getTabCount() {
    return this.knownTabs.size + 1; // +1 for self
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.channel) {
      this.broadcast({ type: 'TAB_LEFT', tabId: this.tabId });
      this.channel.close();
      this.channel = null;
    }
    this.stopLeadershipHeartbeat();
    this.listeners.clear();
    this.knownTabs.clear();
  }
}

// Export singleton instance
export const multiTabCoordinator = new MultiTabCoordinator();
